import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { usePosts } from '../hooks/usePosts';
import { usePostStatusCounts, getApiStatus, POST_STATUS_FILTERS } from '../hooks/usePostStatusCounts';
import Button from '../components/Button';
import Card from '../components/Card';
import FilterButtons from '../components/FilterButtons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { theme } from '../theme/theme';
import useUsers from '../hooks/useUsers';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
  
  // Function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

// Function to get date X days ago
const getDateXDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Function to format date for API (YYYY/MM/DD)
const formatDateForAPI = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const Dashboard = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewType, setViewType] = useState(null); // No default view type
  const [showDateInputs, setShowDateInputs] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState(0);
  const {
    users,
    loading: usersLoading,
    error: usersError,
    fetchUsers,
    getDateRange,
    formatDate: formatUserDate,
    totalCount
  } = useUsers();

  const apiDateFilter = useMemo(() => {
    if (!dateFilter) return null;
    return {
      fromDate: formatDateForAPI(dateFilter.startDate),
      toDate: formatDateForAPI(dateFilter.endDate)
    };
  }, [dateFilter]);

 const apiStatus = getApiStatus(statusFilter);

  const { data: postsData, fetchNextPage, hasNextPage, isFetchingNextPage,isFetching, refetch } = usePosts(
 50,
    apiStatus,
    apiDateFilter,
    true // Always show all data
  );

  // Status-wise counts (respect the date filter, refreshed when a post's status changes)
  const { data: statusCounts, isLoading: countsLoading, isError: countsError } = usePostStatusCounts(apiDateFilter);

  // Fetch users on mount and when dateFilter changes (sync with posts API calls)
  useEffect(() => {
    const fetchAllUsers = async () => {
      let fromDate = '';
      let toDate = '';
      if (dateFilter) {
        fromDate = dateFilter.startDate;
        toDate = dateFilter.endDate;
      }
      await fetchUsers({ fromDate, toDate });
    };
    fetchAllUsers();
  }, [dateFilter, fetchUsers]);

  // Update total users and filtered users
  useEffect(() => {
    if (!dateFilter) {
      setTotalUsers(totalCount);
    } else {
      setFilteredUsers(totalCount);
    }
  }, [dateFilter, totalCount]);

  // Handle view type change
  const handleViewTypeChange = (type) => {
    setViewType(type);
    const currentDate = getCurrentDate();
    setShowDateInputs(false);
    
    switch (type) {
      case 'week':
        setStartDate(getDateXDaysAgo(7));
        setEndDate(currentDate);
        setDateFilter({
          startDate: getDateXDaysAgo(7),
          endDate: currentDate
        });
        break;
      case 'month':
        setStartDate(getDateXDaysAgo(30));
        setEndDate(currentDate);
        setDateFilter({
          startDate: getDateXDaysAgo(30),
          endDate: currentDate
        });
        break;
    }
  };

  // Handle Set Date button click
  const handleSetDateClick = () => {
    setShowDateInputs(true);
    setViewType('custom');
  };

  // Handle Apply button click
  const handleApplyFilter = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        alert('Start date cannot be after end date.');
        return;
      }
      setDateFilter({
        startDate,
        endDate
      });
    }
  };

  // Helper function to check if dates are within a week
  const isWithinWeek = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  // Helper function to check if dates are within a month
  const isWithinMonth = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  // Helper function to format date for daily view
  const formatDailyDate = (date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    return `${day} ${month}`;
  };

  // Helper function to format date for weekly view
  const formatWeeklyDate = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startDay = startOfWeek.getDate();
    const startMonth = startOfWeek.toLocaleString('default', { month: 'short' });
    const endDay = endOfWeek.getDate();
    const endMonth = endOfWeek.toLocaleString('default', { month: 'short' });
    
    return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  };

  // Process posts data for the chart based on createdAt dates
  const processChartData = () => {
    if (!postsData?.pages || postsData.pages.length === 0) return null;
    // Get all items from all pages
    const allItems = postsData.pages
      .flatMap(page => page.items || [])
      .filter(post => apiStatus === 'all' || post.status === apiStatus);
    if (allItems.length === 0) return null;
    // Sort items by createdAt date
    allItems.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    // Get the earliest and latest dates
    const firstDate = new Date(allItems[0].createdAt);
    const lastDate = new Date(allItems[allItems.length - 1].createdAt);
    // Determine the view type based on date range
    const showDailyView = isWithinWeek(firstDate, lastDate);
    const showWeeklyView = !showDailyView && isWithinMonth(firstDate, lastDate);
    if (showDailyView) {
      // Daily view logic
      const intervals = [];
      const currentDate = new Date(firstDate);
      currentDate.setHours(0, 0, 0, 0);
      const endDate = new Date(lastDate);
      endDate.setHours(23, 59, 59, 999);
      while (currentDate <= endDate) {
        intervals.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      // Initialize data structure
      const dailyData = {};
      // Process all items
      allItems.forEach(post => {
        const date = new Date(post.createdAt);
        const dayKey = formatDailyDate(date);
        if (!dailyData[dayKey]) {
          dailyData[dayKey] = { posts: 0, users: new Set() };
        }
        dailyData[dayKey].posts += 1;
        if (post.userId) {
          dailyData[dayKey].users.add(post.userId);
        }
      });
      // Format labels and data
      const labels = intervals.map(date => formatDailyDate(date));
      return {
        labels,
        datasets: [
          {
            label: 'Posts',
            data: labels.map(day => ({
              x: day,
              y: dailyData[day]?.posts || 0,
              posts: dailyData[day]?.posts || 0,
              users: dailyData[day]?.users.size || 0
            })),
            borderColor: '#2e7d32',
            backgroundColor: 'rgba(46, 125, 50, 0.1)',
            tension: 0.4
          }
        ]
      };
    } else if (showWeeklyView) {
      // Weekly view logic
      const intervals = [];
      const currentDate = new Date(firstDate);
      currentDate.setHours(0, 0, 0, 0);
      const endDate = new Date(lastDate);
      endDate.setHours(23, 59, 59, 999);
      while (currentDate <= endDate) {
        intervals.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 7);
      }
      // Initialize data structure
      const weeklyData = {};
      // Process all items
      allItems.forEach(post => {
        const date = new Date(post.createdAt);
        const weekKey = formatWeeklyDate(date);
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = { posts: 0, users: new Set() };
        }
        weeklyData[weekKey].posts += 1;
        if (post.userId) {
          weeklyData[weekKey].users.add(post.userId);
        }
      });
      // Format labels and data
      const labels = intervals.map(date => formatWeeklyDate(date));
      return {
        labels,
        datasets: [
          {
            label: 'Posts',
            data: labels.map(week => ({
              x: week,
              y: weeklyData[week]?.posts || 0,
              posts: weeklyData[week]?.posts || 0,
              users: weeklyData[week]?.users.size || 0
            })),
            borderColor: '#2e7d32',
            backgroundColor: 'rgba(46, 125, 50, 0.1)',
            tension: 0.4
          }
        ]
      };
    } else {
      // Monthly view logic
      const months = [];
      const currentDate = new Date(firstDate);
      while (currentDate <= lastDate) {
        months.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      // Initialize data structure
      const monthlyData = {};
      // Process all items
      allItems.forEach(post => {
        const date = new Date(post.createdAt);
        const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = { posts: 0, users: new Set() };
        }
        monthlyData[monthYear].posts += 1;
        if (post.userId) {
          monthlyData[monthYear].users.add(post.userId);
        }
      });
      // Format labels and data
      const labels = months.map(date => 
        `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`
      );
      return {
        labels,
        datasets: [
          {
            label: 'Posts',
            data: labels.map(month => ({
              x: month,
              y: monthlyData[month]?.posts || 0,
              posts: monthlyData[month]?.posts || 0,
              users: monthlyData[month]?.users.size || 0
            })),
            borderColor: '#2e7d32',
            backgroundColor: 'rgba(46, 125, 50, 0.1)',
            tension: 0.4
          }
        ]
      };
    }
  };

  // Memoized so the chart only re-renders/animates when the data actually changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chartData = useMemo(() => processChartData() || {
    labels: [],
    datasets: [
      {
        label: 'Posts',
        data: [],
        borderColor: '#2e7d32',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        tension: 0.4
      }
    ]
  }, [postsData]);

  // Load any remaining pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Update chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: isWithinWeek(
          new Date(postsData?.pages?.[0]?.items?.[0]?.createdAt || new Date()),
          new Date(postsData?.pages?.[postsData.pages.length - 1]?.items?.[postsData.pages[postsData.pages.length - 1]?.items?.length - 1]?.createdAt || new Date())
        )
          ? 'Daily Activity'
          : isWithinMonth(
              new Date(postsData?.pages?.[0]?.items?.[0]?.createdAt || new Date()),
              new Date(postsData?.pages?.[postsData.pages.length - 1]?.items?.[postsData.pages[postsData.pages.length - 1]?.items?.length - 1]?.createdAt || new Date())
            )
          ? 'Weekly Activity'
          : 'Monthly Activity',
        font: {
          size: 16,
          color: '#000000'
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const data = context.raw;
            return [
              `Posts: ${data.posts}`,
              `Unique Users: ${data.users}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        title: {
          display: true,
          text: 'Count'
        }
      },
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: isWithinWeek(
            new Date(postsData?.pages?.[0]?.items?.[0]?.createdAt || new Date()),
            new Date(postsData?.pages?.[postsData.pages.length - 1]?.items?.[postsData.pages[postsData.pages.length - 1]?.items?.length - 1]?.createdAt || new Date())
          )
            ? 'Day'
            : isWithinMonth(
                new Date(postsData?.pages?.[0]?.items?.[0]?.createdAt || new Date()),
                new Date(postsData?.pages?.[postsData.pages.length - 1]?.items?.[postsData.pages[postsData.pages.length - 1]?.items?.length - 1]?.createdAt || new Date())
              )
            ? 'Week'
            : 'Month'
        }
      },
    },
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme.colors.background.default }}>
      <Sidebar active="dashboard" />
      <main style={{ flex: 1, padding: `${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.xl}`, marginLeft: 280, paddingLeft: theme.spacing.xl, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'auto', fontFamily: theme.typography.fontFamily }}>
        <h2 style={{ ...theme.typography.h2, marginBottom: theme.spacing.sm }}>Dashboard</h2>
        
        {/* Loading indicator */}
        {/* Fixed-height slot so showing/hiding it doesn't shift the layout */}
        <div style={{ minHeight: 24, marginBottom: theme.spacing.md, color: theme.colors.text.secondary }}>
          {(isLoading || isFetching) ? 'Loading data...' : ''}
        </div>
        
        {/* Status-wise counts (clickable filters) */}
        <div role="group" aria-label="Filter posts by status" style={{ display: 'flex', gap: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
          {POST_STATUS_FILTERS.map(({ key, label }) => {
            const isActive = statusFilter === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={isActive}
                onClick={() => setStatusFilter(key)}
                style={{
                  flex: 1,
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: theme.spacing.lg,
                  borderRadius: theme.borderRadius.lg,
                  background: isActive ? '#C6EAAC' : '#DFF0FF',
                  border: `2px solid ${isActive ? '#82C853' : 'transparent'}`,
                  boxShadow: isActive ? theme.shadows.md : theme.shadows.sm,
                  fontFamily: theme.typography.fontFamily,
                  transition: 'background 0.2s, border-color 0.2s'
                }}
              >
                <div style={{ ...theme.typography.h3, color: '#000000CC' }}>
                  {countsLoading ? '…' : countsError ? '–' : statusCounts?.[key] ?? 0}
                </div>
                <div style={{ ...theme.typography.body2, color: theme.colors.text.secondary }}>{label}</div>
              </button>
            );
          })}
        </div>

        {/* User stats */}
        <div style={{ display: 'flex', gap: theme.spacing.lg, marginBottom: theme.spacing.xl }}>
          <Card
            elevation={1}
            padding="lg"
            style={{
              flex: 1, 
              textAlign: 'center',
              background: '#DFF0FF'
            }}
          >
            <div style={{ ...theme.typography.h3, color: '#000000CC' }}>{totalUsers}</div>
            <div style={{ ...theme.typography.body2, color: theme.colors.text.secondary }}>Total Users</div>
          </Card>

          {dateFilter && (
            <>
              <Card
                elevation={1}
                padding="lg"
                style={{
                  flex: 1, 
                  textAlign: 'center',
                  background: '#DFF0FF'
                }}
              >
                <div style={{ ...theme.typography.h3, color: '#000000CC' }}>{filteredUsers}</div>
                <div style={{ ...theme.typography.body2, color: theme.colors.text.secondary }}>Filtered Users</div>
              </Card>
            </>
          )}
        </div>

        {/* Graph */}
        <Card 
          elevation={1}
          padding="lg"
          style={{
            marginBottom: theme.spacing.xl,
            background: theme.colors.background.paper
          }}
        >
          {/* Filter Controls */}
          <FilterButtons
            viewType={viewType}
            onViewTypeChange={handleViewTypeChange}
            onSetDateClick={handleSetDateClick}
            showDateInputs={showDateInputs}
            isLoading={isLoading}
            onApplyFilter={handleApplyFilter}
            onCancel={() => {
              setShowDateInputs(false);
              setViewType(null);
              setDateFilter(null);
              setStartDate('');
              setEndDate(getCurrentDate());
            }}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            getCurrentDate={getCurrentDate}
          />

          <div style={{ ...theme.typography.h3, marginBottom: theme.spacing.md, color: '#000000' }}>
            Activity Over Time
            {statusFilter !== 'all' && ` – ${POST_STATUS_FILTERS.find(f => f.key === statusFilter)?.label} posts`}
          </div>
          <div style={{ height: 300 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard; 